// src/state/editsStore.js
// Stores "Edit" metadata (id, name, timestamps, optional templateSlug)
// Items remain in editStore (pins list), keyed by active edit id.
import { saveEditMeta, deleteEdit as deleteEditApi } from "../lib/editApi";
import { getShopperToken } from "../lib/api";

const KEY = "veya_edits_registry_v1";

function safeRead() {
  try {
    return JSON.parse(localStorage.getItem(KEY) || "null");
  } catch {
    return null;
  }
}

function safeWrite(data) {
  localStorage.setItem(KEY, JSON.stringify(data));
}

function initIfMissing() {
  const cur = safeRead();
  if (cur && Array.isArray(cur.edits)) return cur;

  const base = { edits: [] };
  safeWrite(base);
  return base;
}

export function listEdits() {
  const { edits } = initIfMissing();
  // newest updated first
  return [...edits].sort((a, b) => (b.updatedAt || 0) - (a.updatedAt || 0));
}

export function getEditMeta(id) {
  const { edits } = initIfMissing();
  return edits.find((e) => e.id === id) || null;
}

export function upsertEditMeta(meta) {
  const data = initIfMissing();
  const idx = data.edits.findIndex((e) => e.id === meta.id);
  const next = { ...meta, updatedAt: Date.now() };

  if (idx >= 0) data.edits[idx] = { ...data.edits[idx], ...next };
  else data.edits.unshift(next);

  safeWrite(data);

  if (getShopperToken()) {
    saveEditMeta(next).catch((e) => {
      console.warn("saveEditMeta failed", e);
    });
  }

  return next;
}

export function renameEditMeta(id, name) {
  const data = initIfMissing();
  const idx = data.edits.findIndex((e) => e.id === id);
  if (idx < 0) return null;

  data.edits[idx] = {
    ...data.edits[idx],
    name: String(name || "").trim(),
    updatedAt: Date.now(),
  };

  safeWrite(data);

  if (getShopperToken()) {
    saveEditMeta(data.edits[idx]).catch((e) => {
      console.warn("saveEditMeta failed", e);
    });
  }

  return data.edits[idx];
}

export function deleteEditMeta(id) {
  const data = initIfMissing();
  const before = data.edits.length;
  data.edits = data.edits.filter((e) => e.id !== id);
  safeWrite(data);

  if (before !== data.edits.length && getShopperToken()) {
    deleteEditApi(id).catch((e) => {
      console.warn("deleteEdit failed", e);
    });
  }

  return data.edits.length !== before;
}

export function createEditMeta({ name, templateSlug = null } = {}) {
  const id = `edit_${Date.now()}_${Math.random().toString(16).slice(2)}`;
  const now = Date.now();
  const meta = {
    id,
    // IMPORTANT: allow empty name so UI can show placeholder “Give this edit a name”
    name: String(name || "").trim(),
    createdAt: now,
    updatedAt: now,
    templateSlug,
  };
  upsertEditMeta(meta);
  return meta;
}